import { colors, fonts, radius, sizes, space } from '../../../../theme/tokens'
import { choice, choiceOn } from '../../../../components/choice-look'

import { styles as detailsStyles } from '../step-address/styles'

// Book & pay is the only booking screen anyone is routed to (the
// `booking-flow` experiment was retired on 2026-10-04, see
// client/gwmc/lib/booking-flow.js), so this is THE form: 88.5% of the people
// who open it never pay.
//
// It still inherits the four-step Details screen's card / field / size styling
// so the two look alike. Everything added below is tokens only
// (client/gwmc/DESIGN.md §1): no hex, no rgb(), no space off the scale. The
// inherited `base` is not yet converted, which is why this file reads as two
// halves.
export const styles = theme => {
  const base = detailsStyles(theme)
  return {
    ...base,
    sectionRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: space(8) },

    // ---- Car size: the booking screen's one chosen look
    // (components/choice-look.js). The ring top right is empty until chosen,
    // then a green tick as on the wash cards: the state reads without colour
    // too. Same box in both states, so no layout shift.
    sizeGroup: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: space(12) },
    sizeOption: {
      ...choice,
      borderRadius: radius.control,
      padding: `${space(12)}px ${space(12)}px ${space(12)}px ${space(16)}px`,
      textAlign: 'left',
      fontFamily: fonts.body
    },
    sizeOptionSelected: choiceOn,
    sizeOptionTitle: {
      fontFamily: fonts.body,
      fontSize: 15,
      lineHeight: '20px',
      fontWeight: 600,
      color: colors.ink,
      margin: `0 ${space(24)}px ${space(4)}px 0`
    },
    sizeOptionDesc: {
      fontFamily: fonts.body,
      fontSize: 12,
      lineHeight: '16px',
      color: colors.mute
    },
    sizeTick: {
      position: 'absolute',
      top: space(12),
      right: space(12),
      width: 20,
      height: 20,
      color: colors.mute
    },
    linkBtn: {
      border: 0,
      background: 'none',
      padding: 0,
      color: colors.accent,
      fontFamily: fonts.body,
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer',
      // 44 tall so "Change" is a legal tap target on a phone (DESIGN.md §3).
      // `inline-flex` + minHeight keeps the text on the heading's baseline
      // while the hit box grows above and below it.
      display: 'inline-flex',
      alignItems: 'center',
      minHeight: sizes.tapMin,
      '&:hover': { textDecoration: 'underline' }
    },

    // ---- The page head. ---------------------------------------------------
    // The screen had no H1 at all: the stepper's wordmark, then straight into
    // "YOUR WASH". That breaks DESIGN.md §7 (one H1 per page) and it means the
    // promise that makes this business safe to book was nowhere near the fold.
    pageHead: {
      marginBottom: space(16)
    },
    pageTitle: {
      fontFamily: fonts.display,
      fontSize: 30,
      lineHeight: '36px',
      fontWeight: 400,
      color: colors.ink,
      margin: 0
    },
    // ---- The wash, and what is in it. -------------------------------------
    washRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: space(12) },
    washName: { fontFamily: fonts.body, fontSize: 18, fontWeight: 600, color: colors.ink },
    // Was the retired green. The price is ink, like every other price on the
    // site (DESIGN.md §1: "card prices" are --ink).
    washPrice: { fontFamily: fonts.body, fontSize: 18, fontWeight: 600, color: colors.ink },
    // "Large car +£20" right under the wash's price (lib/price-lines.js).
    washSizeLine: {
      fontFamily: fonts.body,
      fontSize: 14,
      lineHeight: '20px',
      fontWeight: 600,
      color: colors.ink,
      textAlign: 'right',
      margin: `${space(4)}px 0 0`
    },
    washStepsParent: {
      fontFamily: fonts.body,
      fontSize: 13,
      lineHeight: '18px',
      color: colors.mute,
      margin: `${space(8)}px 0 0`
    },

    optionalTag: {
      marginLeft: space(4),
      fontFamily: fonts.body,
      fontSize: 11,
      fontWeight: 400,
      textTransform: 'none',
      letterSpacing: 0,
      color: colors.mute
    },
    addressWrap: { display: 'flex', flexDirection: 'column', gap: space(8), marginBottom: space(16) },

    dayBlock: { marginBottom: space(12) },
    // Your details: the label above a one-line box (booking-screen-trim:
    // "a label above and a one-line input halves the height").
    detailRow: { display: 'flex', flexDirection: 'column' },
    compactField: {
      padding: `0 ${space(12)}px`,
      '& .MuiInputBase-root': { padding: 0, minHeight: sizes.tapMin },
      '& .MuiInputBase-input': { padding: `${space(12)}px 0` },
      '& .MuiFormHelperText-root': { margin: `0 0 ${space(8)}px` }
    },
    signedInLine: {
      margin: `0 0 ${space(8)}px`,
      fontFamily: fonts.body,
      fontSize: 13,
      color: colors.mute
    },

    // ---- Asked at the pay buttons: GONE (31 Oct 2026). ------------------
    // askPanel, askTitle, askSizes, askDate, askNote, sizeHint and sizeError
    // dressed the refused press: the panel at the buttons, its two size
    // cards, its day chips, the red note and the "Choose your car size
    // above" line. No press is refused any more (lib/pay-defaults.js), so
    // there is nothing for them to style. sizeOptionInvalid went with them:
    // the size buttons have no invalid state because the size has no rule.
    addressHint: { margin: 0, fontFamily: fonts.body, fontSize: 13, color: colors.mute },
    // Form v3: what the wash costs for the plate's Large car, in ink.
    carPrice: { margin: `${space(8)}px 0 0`, fontFamily: fonts.body, fontSize: 15, fontWeight: 600, color: colors.ink },

    // The congestion-zone checkbox. MUI's small Checkbox with padding 4 is a
    // ~32px target; --space-8 either side takes the row past --tap-min
    // (DESIGN.md §3).
    checkboxControl: {
      padding: space(8),
      color: colors.mute,
      '&.Mui-checked': { color: colors.accent }
    }
  }
}
